import { Droplets } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dms, inr, niceDate, statusPill } from "./api";
import { PageHeader } from "./OwnerPages";
import { InfoBlock } from "./TeamLeaderPages";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trophy, TrendingUp, ShoppingCart, Percent, Handshake, Users, Store, AlertTriangle, IndianRupee, MapPin, LogIn, LogOut, RefreshCw } from "lucide-react";

// ============================================================================
// RM Dashboard
// ============================================================================
export function RmDashboardPage() {
  const [k, setK] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { dms.rmDashboard().then(d => setK(d.kpis)).catch(() => {}); }, []);
  const [saleL, setSaleL] = useState(null);
  useEffect(() => { dms.rmSaleLiters({}).then(d => setSaleL(d.total_liters ?? 0)).catch(() => {}); }, []);
  const RM = "/dms/regional-manager";
  const cards = [
    { label: "Team Leaders",     value: k?.team_leaders ?? "—",           icon: Users,     tint: "bg-purple-50 text-purple-700", to: `${RM}/team-leaders` },
    { label: "Distributors",     value: k?.distributors ?? "—",           icon: Handshake, tint: "bg-[#faf6e6] text-[#a67c00]", to: `${RM}/distributors` },
    { label: "Retailers",        value: k?.retailers ?? "—",              icon: Store,     tint: "bg-amber-50 text-amber-700", to: `${RM}/retailers` },
    { label: "Salespersons",     value: k?.salespersons ?? "—",           icon: Users,     tint: "bg-fuchsia-50 text-fuchsia-700", to: `${RM}/salespersons` },
    { label: "Today's Order Value", value: k ? inr(k.today_order_value) : "—", icon: ShoppingCart, tint: "bg-sky-50 text-sky-700", to: `${RM}/order-value` },
    { label: "Sale in Liters", value: saleL != null ? `${Number(saleL).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L` : "—", icon: Droplets, tint: "bg-blue-50 text-blue-700", to: RM + "/sale-liters" },
    { label: "Today's Sales (incl. GST)",    value: k ? inr(k.today_sales) : "—",     icon: TrendingUp,tint: "bg-emerald-50 text-emerald-700", to: `${RM}/performance` },
    { label: "Monthly Sales (incl. GST)",    value: k ? inr(k.monthly_sales) : "—",   icon: TrendingUp,tint: "bg-[#faf6e6] text-[#a67c00]", to: `${RM}/performance` },
    { label: "Monthly Sales (excl. GST)",    value: k ? inr(k.monthly_sales_excl_gst || 0) : "—",   icon: TrendingUp,tint: "bg-[#faf6e6] text-[#a67c00]", to: `${RM}/performance` },
    { label: "Outstanding",      value: k ? inr(k.outstanding) : "—",     icon: IndianRupee,tint: "bg-rose-50 text-rose-700", to: `${RM}/outstanding` },
    { label: "Revenue",          value: k ? inr(k.revenue) : "—",         icon: IndianRupee,tint: "bg-indigo-50 text-indigo-700", to: `${RM}/performance` },
    { label: "Fulfillment %",    value: k ? `${k.fulfillment_pct}%` : "—",icon: Percent,   tint: "bg-blue-50 text-blue-700", to: `${RM}/fulfillment` },
    { label: "Today's Collection", value: k ? inr(k.today_collection || 0) : "—", icon: IndianRupee, tint: "bg-teal-50 text-teal-700", to: `${RM}/collections` },
  ];
  return (
    <div>
      <PageHeader title="Regional Manager Dashboard" subtitle="Overview of your region's performance — tap any card to drill in" />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {cards.map(c => (
          <Card key={c.label} onClick={() => navigate(c.to)} data-testid={`rm-kpi-${c.label.replace(/[^a-z]/gi, "").toLowerCase()}`}
            className="p-4 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all">
            <div className={`inline-flex h-9 w-9 rounded-lg items-center justify-center mb-2 ${c.tint}`}><c.icon size={18} /></div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{c.label}</div>
            <div className="text-xl font-bold text-slate-900 mt-1">{c.value}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// RM: Team Leader monitoring (with simple ranking)
// ============================================================================
export function RmTeamLeadersPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { dms.rmTeamLeaders().then(d => setRows(d.data || [])).catch(() => {}); }, []);
  const top = rows[0]?.sales || 0;
  return (
    <div>
      <PageHeader title="Team Leaders" subtitle="Monitoring & simple performance ranking" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Rank</TableHead><TableHead>Team Leader</TableHead>
            <TableHead className="text-right">Sales</TableHead>
            <TableHead className="text-right">Active SPs</TableHead>
            <TableHead className="text-right">Active Dists</TableHead>
            <TableHead className="text-right">Pending</TableHead>
            <TableHead className="text-right">Revenue</TableHead>
            <TableHead>Progress</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No team leaders assigned</TableCell></TableRow>}
            {rows.map((r, i) => (
              <TableRow key={r.id}>
                <TableCell className="font-bold">
                  #{i + 1}
                  {i === 0 && rows.length > 1 && <Trophy size={14} className="inline ml-1 text-amber-500" />}
                </TableCell>
                <TableCell><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500">{r.email}</div></TableCell>
                <TableCell className="text-right font-semibold text-[#8a6600]">{inr(r.sales)}</TableCell>
                <TableCell className="text-right">{r.active_salespersons}</TableCell>
                <TableCell className="text-right">{r.active_distributors}</TableCell>
                <TableCell className="text-right">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${r.pending_orders > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`}>{r.pending_orders}</span>
                </TableCell>
                <TableCell className="text-right">{inr(r.revenue)}</TableCell>
                <TableCell><div className="h-2 w-32 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#faf6e6]0" style={{ width: `${top ? Math.max(2, (r.sales / top) * 100) : 0}%` }} /></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============================================================================
// RM: Region Performance (dist-wise, TL-wise, SP-wise bar charts)
// ============================================================================
export function RmRegionPerformancePage() {
  const [d, setD] = useState({ by_distributor: [], by_team_leader: [], by_salesperson: [] });
  const navigate = useNavigate();
  useEffect(() => { dms.rmRegionPerformance().then(setD).catch(() => {}); }, []);
  const RM = "/dms/regional-manager";
  return (
    <div>
      <PageHeader title="Region Performance" subtitle="Distributor-wise, Team Leader-wise, and Salesperson-wise sales — tap a card for details" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BarBlock title="By Distributor" rows={d.by_distributor} onOpen={() => navigate(`${RM}/distributors`)} testid="perf-distributors" />
        <BarBlock title="By Team Leader" rows={d.by_team_leader} onOpen={() => navigate(`${RM}/team-leaders`)} testid="perf-teamleaders" />
        <BarBlock title="By Salesperson" rows={d.by_salesperson} onOpen={() => navigate(`${RM}/salespersons`)} testid="perf-salespersons" />
      </div>
    </div>
  );
}

function BarBlock({ title, rows, onOpen, testid }) {
  const max = Math.max(1, ...rows.map(r => r.sales));
  return (
    <Card onClick={onOpen} data-testid={testid} className={onOpen ? "cursor-pointer hover:shadow-md transition-shadow" : ""}>
      <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900 flex items-center justify-between">
        {title}
        {onOpen && <span className="text-xs text-[#a67c00] font-medium">View details →</span>}
      </div>
      <div className="p-4 space-y-3">
        {rows.length === 0 && <div className="text-center py-6 text-sm text-slate-400">No data yet</div>}
        {rows.map(r => (
          <div key={r.id}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="truncate">{r.name}</span>
              <span className="font-semibold text-slate-900">{inr(r.sales)}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#faf6e6]0" style={{ width: `${Math.max(2, (r.sales / max) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ============================================================================
// RM: Distributor monitoring (read-only)
// ============================================================================
export function RmDistributorsPage() {
  const [rows, setRows] = useState([]);
  const [stockFor, setStockFor] = useState(null); // distributor obj or null
  const [stockRows, setStockRows] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  useEffect(() => { dms.rmDistributors().then(d => setRows(d.data || [])).catch(() => {}); }, []);

  const openStock = async (d) => {
    setStockFor(d);
    setStockLoading(true);
    try {
      const res = await dms.rmDistributorInventory(d.id);
      setStockRows(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load stock");
      setStockRows([]);
    }
    setStockLoading(false);
  };

  return (
    <div>
      <PageHeader title="Distributor Monitoring" subtitle="Read-only view of all distributors in your region" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Distributor</TableHead>
            <TableHead className="text-right">Stock</TableHead>
            <TableHead className="text-right">Pending Payments</TableHead>
            <TableHead className="text-right">Revenue</TableHead>
            <TableHead className="text-right">Retailers</TableHead>
            <TableHead className="text-right">Orders</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">None</TableCell></TableRow>}
            {rows.map(d => (
              <TableRow key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openStock(d)} data-testid={`rm-dist-row-${d.id}`}>
                <TableCell><div className="font-medium">{d.name}</div><div className="text-xs text-slate-500">{d.region}</div></TableCell>
                <TableCell className="text-right underline decoration-dotted">{fmtBoxes(d.stock)} boxes</TableCell>
                <TableCell className="text-right text-rose-700">{inr(d.pending_payments)}</TableCell>
                <TableCell className="text-right font-semibold">{inr(d.revenue)}</TableCell>
                <TableCell className="text-right">{d.retailers}</TableCell>
                <TableCell className="text-right">{d.orders}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!stockFor} onOpenChange={o => { if (!o) { setStockFor(null); setStockRows([]); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Stock — {stockFor?.name}</DialogTitle></DialogHeader>
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Product</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Boxes</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {stockLoading && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>}
                {!stockLoading && stockRows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">No stock records</TableCell></TableRow>}
                {stockRows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{r.product_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.sku_code}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.unit}</TableCell>
                    <TableCell className="text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.qty_boxes < 5 ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {fmtBoxes(r.qty_boxes)} boxes
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// RM: Salesperson monitoring (read-only, live status)
// ============================================================================
export function RmSalespersonsPage() {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { dms.rmSalespersons().then(d => setRows(d.data || [])).catch(() => {}); }, []);
  return (
    <div>
      <PageHeader title="Salesperson Monitoring" subtitle="Read-only view: attendance, orders, visits, new retailers — tap a card to see their orders" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {rows.length === 0 && <Card className="p-8 text-center col-span-full text-sm text-slate-500">No salespersons in region</Card>}
        {rows.map(s => (
          <Card key={s.id} className="p-4 cursor-pointer hover:shadow-md transition" onClick={() => navigate(`/dms/regional-manager/orders?salesperson_id=${s.id}`)} data-testid={`rm-sp-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900">{s.name}</div>
                <div className="text-xs text-slate-500">{s.phone}</div>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${s.online ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>{s.online ? "● Online" : "○ Offline"}</span>
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <Line icon={LogIn}  label="Punch In"  value={s.punch_in ? niceDate(s.punch_in) : "—"} />
              <Line icon={LogOut} label="Punch Out" value={s.punch_out ? niceDate(s.punch_out) : "—"} />
              <Line icon={MapPin} label="Location"  value={s.live_location ? `${s.live_location.lat?.toFixed(3)}, ${s.live_location.lng?.toFixed(3)}` : "—"} />
              <Line icon={Store}  label="Visits"    value={s.today_visits} />
              <Line icon={ShoppingCart} label="Orders" value={s.orders_today} />
              <Line icon={Users}  label="New Retailers" value={s.new_retailers_today} />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Line({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-slate-600"><Icon size={12} className="text-slate-400" /> {label}</span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}

// ============================================================================
// RM — Products (Price List, read-only — NO stock quantities shown)
// ============================================================================
export function RmProductsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    dms.listProducts().then(d => setList(d.data || [])).finally(() => setLoading(false));
  }, []);

  const filtered = list.filter(p => {
    if (!q) return true;
    const s = q.toLowerCase();
    const hay = `${p.material_description || p.name} ${p.grade_specs} ${p.pack_size} ${p.category_name}`.toLowerCase();
    return hay.includes(s);
  });

  return (
    <div>
      <PageHeader title="Products" subtitle={`${list.length} products \u2022 price reference (stock not shown)`} />
      <div className="mb-4">
        <input
          className="w-full max-w-sm rounded-md border border-slate-200 px-3 py-2 text-sm"
          placeholder="Search Material / Grade / Pack\u2026"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
      </div>
      {loading && <div className="p-8 text-center text-sm text-slate-500">Loading\u2026</div>}
      {!loading && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead className="min-w-[200px]">Material Description</TableHead>
                  <TableHead>Grade / Specs</TableHead>
                  <TableHead>Pack Size</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">MRP</TableHead>
                  <TableHead className="text-right">Price (DLP)</TableHead>
                  <TableHead>GST %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(p => (
                  <TableRow key={p.id} className="hover:bg-slate-50/60">
                    <TableCell className="font-medium text-slate-900">{p.material_description || p.name}</TableCell>
                    <TableCell><span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-mono">{p.grade_specs || "-"}</span></TableCell>
                    <TableCell className="text-slate-700 font-medium">{p.pack_size || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{p.category_name || "-"}</TableCell>
                    <TableCell className="text-right text-slate-700">{p.mrp ? inr(p.mrp) : "\u2014"}</TableCell>
                    <TableCell className="text-right font-bold text-[#8a6600]">{p.unit_price ? inr(p.unit_price) : "\u2014"}</TableCell>
                    <TableCell className="text-xs text-slate-600">{p.gst_pct ? `${p.gst_pct}%` : "\u2014"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// RM: Order Monitoring — across all assigned Team Leaders / distributors
// ============================================================================
export function RmOrdersMonitoringPage() {
  const [orders, setOrders] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [f, setF] = useState({ status: "", team_leader_id: "", salesperson_id: "" });
  const [detailOrder, setDetailOrder] = useState(null);

  // Support ?salesperson_id=… deep-link from Salesperson Monitoring cards
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const spid = sp.get("salesperson_id");
    if (spid) setF(x => ({ ...x, salesperson_id: spid }));
  }, []);

  const refresh = () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    dms.rmOrders(params).then(d => setOrders(d.data || [])).catch(() => {});
  };
  useEffect(() => {
    refresh();
    dms.rmTeamLeaders().then(d => setTeamLeaders(d.data || [])).catch(() => {});
    dms.rmSalespersons().then(d => setSalespersons(d.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [f]);

  const openDetail = async (o) => {
    try {
      const full = await dms.getSecondaryOrder(o.id);
      setDetailOrder(full);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load order"); }
  };

  return (
    <div>
      <PageHeader title="Order Monitoring" subtitle="All secondary orders across your region — click any row for full details" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
        <Select value={f.status || "__all__"} onValueChange={v => setF({ ...f, status: v === "__all__" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="invoiced">Invoiced</SelectItem>
            <SelectItem value="dispatched">Dispatched</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={f.team_leader_id || "__all__"} onValueChange={v => setF({ ...f, team_leader_id: v === "__all__" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="All team leaders" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All team leaders</SelectItem>
            {teamLeaders.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={f.salesperson_id || "__all__"} onValueChange={v => setF({ ...f, salesperson_id: v === "__all__" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="All salespersons" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All salespersons</SelectItem>
            {salespersons.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Distributor</TableHead>
              <TableHead>Retailer</TableHead>
              <TableHead>Placed By</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Placed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No orders match filters</TableCell></TableRow>}
            {orders.map(o => (
              <TableRow key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(o)} data-testid={`rm-order-row-${o.id}`}>
                <TableCell className="font-mono text-xs">{o.order_no || o.id}</TableCell>
                <TableCell>{o.distributor_name}</TableCell>
                <TableCell>{o.retailer_name}</TableCell>
                <TableCell className="text-sm">{o.placed_by_name || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="text-right font-medium">{inr(o.total)}</TableCell>
                <TableCell><span className={`px-2 py-0.5 rounded-full text-xs border ${statusPill(o.status)}`}>{o.status}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(o.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detailOrder} onOpenChange={o => { if (!o) setDetailOrder(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order {detailOrder?.order_no}</DialogTitle>
          </DialogHeader>
          {detailOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Retailer</div>
                  <div className="text-slate-900 font-medium">{detailOrder.retailer?.name || detailOrder.retailer_name}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Distributor</div>
                  <div className="text-slate-900 font-medium">{detailOrder.distributor?.name}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Placed By</div>
                  <div className="text-slate-900 font-medium">{detailOrder.placed_by_name || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Placed At</div>
                  <div className="text-slate-900 font-medium">{niceDate(detailOrder.created_at)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Status</div>
                  <span className={`px-2 py-0.5 rounded-full text-xs border ${statusPill(detailOrder.status)}`}>{detailOrder.status}</span>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Total</div>
                  <div className="text-lg font-bold text-[#a67c00]">{inr(detailOrder.total)}</div>
                </div>
              </div>
              {detailOrder.cancel_reason && (
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800">
                  <b>Cancelled:</b> {detailOrder.cancel_reason}
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Items ({detailOrder.items?.length || 0})</div>
                <Card className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead className="text-right">Boxes</TableHead>
                        <TableHead className="text-right">Nos</TableHead>
                        <TableHead className="text-right">Line Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(detailOrder.items || []).map((it, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{it.product_name}</TableCell>
                          <TableCell className="font-mono text-xs">{it.sku_code}</TableCell>
                          <TableCell className="text-right">{it.qty_boxes_ordered}</TableCell>
                          <TableCell className="text-right">{it.qty_pcs_ordered || 0}</TableCell>
                          <TableCell className="text-right font-medium">{inr(it.line_total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOrder(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// RM: Outstanding — distributor-wise, drill into retailer-wise breakdown
// ============================================================================
export function RmOutstandingPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [distFor, setDistFor] = useState(null);
  const [retRows, setRetRows] = useState([]);
  const [retTotal, setRetTotal] = useState(0);
  const [retLoading, setRetLoading] = useState(false);

  useEffect(() => {
    dms.rmOutstanding().then(d => { setRows(d.data || []); setTotal(d.total || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openDistributor = async (d) => {
    setDistFor(d);
    setRetLoading(true);
    try {
      const res = await dms.rmOutstandingRetailers(d.distributor_id);
      setRetRows(res.data || []);
      setRetTotal(res.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load retailers");
      setRetRows([]);
    }
    setRetLoading(false);
  };

  return (
    <div>
      <PageHeader title="Outstanding" subtitle="Distributor-wise retailer outstanding — tap a row to see retailer breakdown"
        action={<Button variant="outline" size="sm" onClick={() => nav("/dms/regional-manager/outstanding-detail")}>Retailer Detail View</Button>} />
      <Card className="p-4 mb-4 bg-rose-50 border-rose-200">
        <div className="text-xs uppercase tracking-wider text-rose-700">Total Outstanding</div>
        <div className="text-2xl font-bold text-rose-900 mt-1">{inr(total)}</div>
        <div className="text-xs text-rose-700 mt-0.5">{rows.length} distributor{rows.length === 1 ? "" : "s"}</div>
      </Card>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Distributor</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={2} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={2} className="text-center py-8 text-slate-400">No outstanding — all clear</TableCell></TableRow>}
            {rows.map(d => (
              <TableRow key={d.distributor_id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDistributor(d)} data-testid={`rm-outstanding-row-${d.distributor_id}`}>
                <TableCell className="font-medium underline decoration-dotted">{d.distributor_name}</TableCell>
                <TableCell className="text-right font-semibold text-rose-700">{inr(d.outstanding)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!distFor} onOpenChange={o => { if (!o) { setDistFor(null); setRetRows([]); } }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Outstanding — {distFor?.distributor_name}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 mb-2">Total: <span className="font-semibold text-rose-700">{inr(retTotal)}</span></div>
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Retailer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {retLoading && <TableRow><TableCell colSpan={3} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>}
                {!retLoading && retRows.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-slate-400">No outstanding retailers</TableCell></TableRow>}
                {retRows.map(r => (
                  <TableRow key={r.retailer_id}>
                    <TableCell className="font-medium">{r.retailer_name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.phone || "—"}</TableCell>
                    <TableCell className="text-right font-semibold text-rose-700">{inr(r.outstanding)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDistFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// RM: Fulfillment — retailer-wise invoiced vs pending order value
// ============================================================================
export function RmFulfillmentPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_orders: 0, total_invoiced: 0, total_pending: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dms.rmFulfillment().then(d => {
      setRows(d.data || []);
      setSummary({ total_orders: d.total_orders || 0, total_invoiced: d.total_invoiced || 0, total_pending: d.total_pending || 0 });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const overallPct = summary.total_orders ? Math.round((summary.total_invoiced / summary.total_orders) * 1000) / 10 : 0;

  return (
    <div>
      <PageHeader title="Fulfillment" subtitle="Retailer-wise: how much has been invoiced vs how much is still pending" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Card className="p-4 bg-slate-50">
          <div className="text-xs uppercase tracking-wider text-slate-500">Total Order Value</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{inr(summary.total_orders)}</div>
        </Card>
        <Card className="p-4 bg-emerald-50 border-emerald-200">
          <div className="text-xs uppercase tracking-wider text-emerald-700">Invoiced</div>
          <div className="text-xl font-bold text-emerald-900 mt-1">{inr(summary.total_invoiced)}</div>
          <div className="text-xs text-emerald-700 mt-0.5">{overallPct}% of total</div>
        </Card>
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="text-xs uppercase tracking-wider text-amber-700">Still Pending</div>
          <div className="text-xl font-bold text-amber-900 mt-1">{inr(summary.total_pending)}</div>
        </Card>
      </div>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Retailer</TableHead>
            <TableHead>Distributor</TableHead>
            <TableHead className="text-right">Total Order Value</TableHead>
            <TableHead className="text-right">Invoiced</TableHead>
            <TableHead className="text-right">Pending</TableHead>
            <TableHead className="text-right">Fulfillment %</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No orders found</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.retailer_id}>
                <TableCell className="font-medium">{r.retailer_name}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.distributor_name}</TableCell>
                <TableCell className="text-right">{inr(r.total_order_value)}</TableCell>
                <TableCell className="text-right text-emerald-700 font-medium">{inr(r.invoiced_value)}</TableCell>
                <TableCell className="text-right text-amber-700 font-medium">{inr(r.pending_value)}</TableCell>
                <TableCell className="text-right">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.fulfillment_pct >= 100 ? "bg-emerald-100 text-emerald-800" : r.fulfillment_pct === 0 ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                    {r.fulfillment_pct}%
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============================================================================
// RM: Outstanding Detail — retailer-level, filterable by TL/distributor/salesperson/as-of-date
// ============================================================================
export function RmOutstandingDetailPage() {
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [teamLeaderId, setTeamLeaderId] = useState("");
  const [distributorId, setDistributorId] = useState("");
  const [salespersonId, setSalespersonId] = useState("");
  const [asOfDate, setAsOfDate] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dms.rmTeamLeaders().then(d => setTeamLeaders(d.data || [])).catch(() => {});
    dms.rmDistributors().then(d => setDistributors(d.data || [])).catch(() => {});
    dms.rmSalespersons().then(d => setSalespersons(d.data || [])).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (teamLeaderId) params.team_leader_id = teamLeaderId;
    if (distributorId) params.distributor_id = distributorId;
    if (salespersonId) params.salesperson_id = salespersonId;
    if (asOfDate) params.as_of_date = asOfDate;
    dms.rmOutstandingDetail(params)
      .then(d => { setRows(d.data || []); setTotal(d.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [teamLeaderId, distributorId, salespersonId, asOfDate]);

  return (
    <div>
      <PageHeader title="Outstanding — Retailer Detail" subtitle="Filter by team leader, distributor, salesperson, or as-of date" back="/dms/regional-manager" />

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-xs">Team Leader</Label>
          <select value={teamLeaderId} onChange={e => setTeamLeaderId(e.target.value)}
                  className="mt-1 flex h-9 w-[180px] rounded-md border border-input bg-background px-3 py-1 text-sm">
            <option value="">All Team Leaders</option>
            {teamLeaders.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Distributor</Label>
          <select value={distributorId} onChange={e => setDistributorId(e.target.value)}
                  className="mt-1 flex h-9 w-[180px] rounded-md border border-input bg-background px-3 py-1 text-sm">
            <option value="">All Distributors</option>
            {distributors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Salesperson</Label>
          <select value={salespersonId} onChange={e => setSalespersonId(e.target.value)}
                  className="mt-1 flex h-9 w-[180px] rounded-md border border-input bg-background px-3 py-1 text-sm">
            <option value="">All Salespersons</option>
            {salespersons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">As Of Date</Label>
          <Input type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)} className="mt-1" />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw size={14} className="mr-1" /> Refresh</Button>
      </div>

      <Card className="p-4 mb-4 bg-rose-50 border-rose-200">
        <div className="text-xs uppercase tracking-wider text-rose-700">Total Outstanding</div>
        <div className="text-2xl font-bold text-rose-900 mt-1">{inr(total)}</div>
        <div className="text-xs text-rose-700 mt-0.5">{rows.length} retailer{rows.length === 1 ? "" : "s"}</div>
      </Card>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Retailer</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Distributor</TableHead>
            <TableHead>Salesperson</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No outstanding — all clear</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.retailer_id}>
                <TableCell className="font-medium">{r.retailer_name}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.phone || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.distributor_name}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.salesperson_name || "—"}</TableCell>
                <TableCell className="text-right font-semibold text-rose-700">{inr(r.outstanding)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}


function fmtBoxes(v) {
  const n = Number(v);
  if (!isFinite(n)) return "0";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
